import { useState } from 'react';
import {
  DonViCoSo,
  HanThanhToan,
  KhachHang,
  KhoHang,
  NhanVien,
  PhuongThucThanhToan,
  QuyenChungTu
} from '@/types/schemas';

export interface FormFieldState {
  khachHang: MuaHangRecord | null;
  nhanVien: MuaHangRecord | null;
  quyenChungTu: QuyenChungTu | null;

  donViCoSo: DonViCoSo | null;

  diaChi: MuaHangRecord | null;
  khoHang: KhoHang | null;
  phuongThucThanhToan: PhuongThucThanhToan | null;
  hanThanhToan: MuaHangRecord | null;

  soChungTu: string;
}

export interface FormFieldActions {
  setKhachHang: (khachHang: MuaHangRecord) => void;
  setNhanVien: (nhanVien: MuaHangRecord) => void;
  setQuyenChungTu: (quyenChungTu: QuyenChungTu) => void;

  setDonViCoSo: (donViCoSo: DonViCoSo) => void;

  setDiaChi: (diaChi: MuaHangRecord) => void;
  setKhoHang: (khoHang: KhoHang) => void;
  setPhuongThucThanhToan: (phuongThucThanhToan: PhuongThucThanhToan) => void;
  setHanThanhToan: (hanThanhToan: MuaHangRecord) => void;

  setSoChungTu: (soChungTu: string) => void;

  // Utility functions
  resetState: () => void;
  updateState: (updates: Partial<FormFieldState>) => void;

  refreshAllPriceLookups: () => void;
}

export interface UseFormFieldStateReturn {
  state: FormFieldState;
  actions: FormFieldActions;
}

const initialState: FormFieldState = {
  khachHang: null,
  nhanVien: null,
  quyenChungTu: null,

  donViCoSo: null,

  diaChi: null,
  khoHang: null,
  phuongThucThanhToan: null,
  hanThanhToan: null,

  soChungTu: ''
};

function transformInitialData(initialData?: MuaHangRecord): Partial<FormFieldState> {
  if (!initialData) return {};

  return {
    khachHang: initialData.ma_kh_data || initialData.ma_ncc_data || null,
    nhanVien: initialData.ma_nvmh_data || null,
    quyenChungTu: initialData.ma_nk_data || null,

    donViCoSo: initialData.unit_id_data || null,

    diaChi: initialData.ma_dcnh_data || initialData.noi_nhan_data || null,
    khoHang: initialData.ma_kho_data || null,
    phuongThucThanhToan: initialData.ma_pttt_data || initialData.phuong_thuc_thanh_toan_data || null,
    hanThanhToan: initialData.ma_tt_data || initialData.han_thanh_toan_data || null,

    soChungTu: initialData.so_ct || ''
  };
}

export function useFormFieldState(initialData?: MuaHangRecord): UseFormFieldStateReturn {
  const [state, setState] = useState<FormFieldState>({
    ...initialState,
    ...transformInitialData(initialData)
  });

  const actions: FormFieldActions = {
    // Search field setters
    setKhachHang: (khachHang: KhachHang) => {
      setState(prev => ({
        ...prev,
        khachHang
      }));
    },

    setNhanVien: (nhanVien: NhanVien) => {
      setState(prev => ({
        ...prev,
        nhanVien
      }));
    },

    setQuyenChungTu: (quyenChungTu: QuyenChungTu) => {
      setState(prev => ({
        ...prev,
        quyenChungTu
      }));
    },

    setDonViCoSo: (donViCoSo: DonViCoSo) => {
      setState(prev => ({
        ...prev,
        donViCoSo
      }));
    },

    setDiaChi: (diaChi: MuaHangRecord) => {
      setState(prev => ({
        ...prev,
        diaChi
      }));
    },

    setKhoHang: (khoHang: KhoHang) => {
      setState(prev => ({
        ...prev,
        khoHang
      }));
    },

    setPhuongThucThanhToan: (phuongThucThanhToan: PhuongThucThanhToan) => {
      setState(prev => ({
        ...prev,
        phuongThucThanhToan
      }));
    },

    setHanThanhToan: (hanThanhToan: HanThanhToan) => {
      setState(prev => ({
        ...prev,
        hanThanhToan
      }));
    },

    setSoChungTu: (soChungTu: string) => {
      setState(prev => ({
        ...prev,
        soChungTu
      }));
    },

    resetState: () => {
      setState(initialState);
    },

    updateState: (updates: Partial<FormFieldState>) => {
      setState(prev => ({ ...prev, ...updates }));
    },

    refreshAllPriceLookups: () => {
      setState(prev => ({ ...prev, refreshAllPriceLookups: true }));
    }
  };

  return { state, actions };
}
